import React, { useState } from 'react';
import 'twin.macro';
import Drawer from 'components/Drawer';
import { LimitReached } from 'components/LimitReached';

import Button from 'components/Buttons/Button';
import { SequencesTree } from 'containers/Sequences/List/Sidebar/SequencesTree';
import { useNavigate } from 'react-router-dom';
import { useCreateFicheDePrep } from 'queries/fdp/useCreateFichedePrep';
import { Trans, useTranslation } from 'react-i18next';
import { CJEvent } from 'types/event';
import { Infobox } from 'components/Callout/Infobox';

import { getFicheDePrepFromCJEvent } from './getFicheDePrepFromCJEvent';
import { useSequenceCustomFieldDefinitions } from 'queries/sequences/customfields/useSequenceCustomFieldDefinitions';
import { useUpdateSequenceCustomFieldDefinition } from 'queries/sequences/customfields/useUpdateSequenceCustomFieldDefinition';
import { useEventCustomFieldDefinitions } from 'queries/events/customfields/useEventCustomFieldDefinitions';
import { useSchoolSchoolyear } from 'containers/App/context/SchoolyearSchoolProvider';
import { getTargetCustomFieldsFromSource } from '../EventModal/ModalBody/Classic/LinkedFichesDePreps/getCJSeanceCustomFieldsFromFdpForNewCF';
import { useCreateStandaloneFicheDePrep } from 'queries/fdp/useCreateStandaloneFicheDePrep';
import { lineBreak, italic } from 'i18n/components';

function DrawerBody({
  seance,
  onClose,
}: {
  seance?: CJEvent;
  onClose: () => void;
}) {
  const [selectedItem, setSelectedItem] = useState<{
    id: string;
    type: 'folder' | 'sequence';
    name: string;
  } | null>(null);
  const { mutateAsync: createFdpStandalone } = useCreateStandaloneFicheDePrep();
  const navigate = useNavigate();
  const createFicheDePrep = useCreateFicheDePrep();
  const { t } = useTranslation(['common', 'cahierJournal']);
  const { data: sequenceCustomFieldDefinitions = [] } =
    useSequenceCustomFieldDefinitions();
  const { mutateAsync: onUpdateDefinition } =
    useUpdateSequenceCustomFieldDefinition();
  const { school, schoolyear } = useSchoolSchoolyear();
  const { data: eventCustomFieldDefinitions = [] } =
    useEventCustomFieldDefinitions({
      schoolId: school.id,
      schoolyearId: schoolyear.id,
    });

  const { updatedCustomFields, missingCustomFieldOptions } =
    getTargetCustomFieldsFromSource(
      seance?.customFields || [],
      eventCustomFieldDefinitions,
      [],
      sequenceCustomFieldDefinitions,
    );

  return (
    <div tw="flex flex-col h-full p-2 space-y-4 min-h-0">
      <div tw="shrink-0">
        <Infobox intent="primary" tw="mt-4">
          {t('cahierJournal:createFicheDePrepDrawer.callout')}
        </Infobox>
      </div>
      <div data-testid="sequence-tree" tw="flex-1 overflow-y-auto">
        <SequencesTree
          visibleTypes={['folder', 'sequence']}
          hideActions
          elementId={selectedItem?.id}
          onClickFolder={(folderId, folderName) => {
            if (selectedItem?.id === folderId) {
              setSelectedItem(null);
              return;
            }
            setSelectedItem({
              id: folderId,
              type: 'folder',
              name: folderName,
            });
          }}
          onClickSequence={(sequenceId, sequenceName) => {
            if (selectedItem?.id === sequenceId) {
              setSelectedItem(null);
              return;
            }
            setSelectedItem({
              id: sequenceId,
              type: 'sequence',
              name: sequenceName,
            });
          }}
          hiddenActions="all"
        />
      </div>
      <div tw="shrink-0">
        <Button
          onClick={() => {
            if (!seance) {
              return;
            }
            const updatePromises = Object.entries(
              missingCustomFieldOptions,
            ).map(([definitionId, options]) => {
              const definition = sequenceCustomFieldDefinitions.find(
                def => def.id === definitionId,
              );
              if (!definition) return Promise.resolve();
              return onUpdateDefinition({
                ...definition,
                options: [...(definition.options || []), ...options],
              });
            });

            if (selectedItem?.type === 'sequence') {
              return Promise.all(updatePromises).then(() =>
                createFicheDePrep({
                  sequenceId: selectedItem.id,
                  ficheDePrep: {
                    ...getFicheDePrepFromCJEvent(seance, selectedItem.id),
                    customFields: updatedCustomFields,
                  },
                }).then(fdp => {
                  onClose();
                  navigate(
                    `/sequences/${fdp.sequenceId}/fiches-de-prep/${fdp.id}`,
                  );
                }),
              );
            }

            return Promise.all(updatePromises).then(() =>
              createFdpStandalone({
                ficheDePrep: {
                  ...getFicheDePrepFromCJEvent(seance),
                  customFields: updatedCustomFields,
                },
                folderId:
                  selectedItem?.type === 'folder'
                    ? selectedItem?.id
                    : undefined,
              }).then(fdp => {
                onClose();
                navigate(`/fiches-de-prep/${fdp.id}`);
              }),
            );
          }}
          fill
          large
          intent="primary"
        >
          <span>
            {selectedItem ? (
              <Trans
                i18nKey={
                  selectedItem.type === 'folder'
                    ? 'cahierJournal:createFicheDePrepDrawer.createInFolder'
                    : 'cahierJournal:createFicheDePrepDrawer.createInSequence'
                }
                values={{ target: selectedItem.name }}
                components={{ lineBreak, italic }}
              />
            ) : (
              <Trans
                i18nKey="cahierJournal:createFicheDePrepDrawer.createInRoot"
                components={{ lineBreak }}
              />
            )}
          </span>
        </Button>
      </div>
    </div>
  );
}

interface Props {
  isOpen: boolean;
  seance?: CJEvent;
  onClose: () => void;
}

export function FdpDrawer(props: Props) {
  const { isOpen, seance, onClose } = props;
  const { t } = useTranslation(['common', 'cahierJournal']);

  return (
    <>
      {isOpen && <LimitReached item="seance" />}
      <Drawer
        title={t('cahierJournal:createFicheDePrepDrawer.title')}
        position="right"
        isOpen={isOpen}
        onClose={onClose}
        size="md"
      >
        <DrawerBody seance={seance} onClose={onClose} />
      </Drawer>
    </>
  );
}
